import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { StatusChip } from '../components/app/StatusChip';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw, Lock, Unlock, Calendar } from 'lucide-react';

const VALID_TRANSITIONS = {
  OPEN: ['LOCKED'],
  STAGED: ['OPEN', 'LOCKED'],
  ACTIVE: ['LOCKED', 'OPEN'],
  SUBMITTED: ['APPROVED', 'ACTIVE', 'LOCKED'],
  APPROVED: ['LOCKED', 'EXPORTED'],
  LOCKED: ['OPEN', 'ACTIVE'],
  EXPORTED: ['OPEN'],
};

function PeriodDialog({ open, onClose, onSave, initial }) {
  const [form, setForm] = useState({ label: '', period_start: '', period_end: '', description: '' });
  useEffect(() => { if (open) setForm(initial || { label: '', period_start: '', period_end: '', description: '' }); }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>{initial ? 'Edit' : 'New'} Statement Period</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">Period Label *</Label><Input value={form.label} onChange={e=>set('label',e.target.value)} className="mt-1 h-8 text-sm" placeholder="e.g. 2026-02" data-testid="period-label-input" /></div>
          <div><Label className="text-xs">Period Start *</Label><Input type="date" value={form.period_start} onChange={e=>set('period_start',e.target.value)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Period End *</Label><Input type="date" value={form.period_end} onChange={e=>set('period_end',e.target.value)} className="mt-1 h-8 text-sm" /></div>
          <div><Label className="text-xs">Description</Label><Input value={form.description} onChange={e=>set('description',e.target.value)} className="mt-1 h-8 text-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => onSave(form)} data-testid="period-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PeriodsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [statusChangePeriod, setStatusChangePeriod] = useState(null);
  const [newStatus, setNewStatus] = useState('');

  const load = async () => {
    setLoading(true);
    try { const r = await API.get('/periods'); setItems(r.data); }
    catch(e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (editing) { toast.info('Period editing not supported after creation'); }
      else { await API.post('/periods', form); toast.success('Period created'); }
      setDialogOpen(false); setEditing(null); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const changeStatus = async (pid, status) => {
    try {
      await API.put(`/periods/${pid}/status`, null, { params: { status } });
      toast.success(`Period status set to ${status}`);
      load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Status change failed'); }
  };

  const doDelete = async () => {
    try { await API.delete(`/periods/${deleteId}`); toast.success('Deleted'); setDeleteId(null); load(); }
    catch(e) { toast.error('Delete failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Statement Periods</h1>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-period-btn">
          <Plus size={14} className="mr-1" /> New Period
        </Button>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="periods-table">
          <thead><tr>
            <th>Label</th><th>Period Start</th><th>Period End</th><th>Status</th><th>Created By</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400"><RefreshCw size={16} className="animate-spin inline" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-400">No periods created yet. Create your first statement period.</td></tr>
            ) : items.map(p => (
              <tr key={p.id}>
                <td className="font-semibold">{p.label}</td>
                <td>{p.period_start}</td>
                <td>{p.period_end}</td>
                <td><StatusChip status={p.status} size="xs" /></td>
                <td className="text-sm text-slate-500">{p.created_by}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {(VALID_TRANSITIONS[p.status] || []).map(s => (
                      <button
                        key={s}
                        onClick={() => changeStatus(p.id, s)}
                        className="text-[10px] px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50 text-slate-600"
                        title={`Set to ${s}`}
                      >{s === 'LOCKED' ? <Lock size={11} /> : <Unlock size={11} />} {s}</button>
                    ))}
                    <button onClick={() => setDeleteId(p.id)} className="p-1 text-slate-400 hover:text-rose-600 ml-1"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PeriodDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }} onSave={save} initial={editing} />
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Delete Period?</AlertDialogTitle><AlertDialogDescription>All imports and transactions linked to this period will be affected.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-rose-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
